import { Box, Flex } from "@mantine/core";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import PixBimViewer from "../components/PixBimViewer/PixBimViewer";
import {
  selectFinalPixPolygons,
  selectFloorExtentKey,
  selectPixActionState,
  selectPixPaths,
} from "./pixpolygonSelectors";
import { selectModRotation } from "./designSelector";
import { selectnrOfMcbimSpaces } from "../McBim/mcbimSelectors";
import { selectNrOfDevices } from "./devices/deviceSelectors";
import { dragSelectEndThunk, thunkClickOnAPixPolygon, thunkHandleRightMenu } from "./designThunk";
import { thunkMoveDeviceOnDragEnd, thunkStartMove } from "./devices/deviceThunks";
import { useGetMultiplacerDevicetypesQuery, usePostMcbimItemsQuery } from "../../openapi";
import { selectGetMcBimCeilingDevicesArgs, selectGetMcBimItemsArkArgs } from "../McBim/McBimSlice";
import { skipToken } from "@reduxjs/toolkit/query";
import { RIGHT_CLICK_DEVICE_MENU_ENUM } from "../enums";
import { selectProjectNo } from "../Room/RoomSlice";
import { IS_DEV } from "../config";

const parseNum = (s: string) => {
  const n = Number(s.replace(",", "."));
  return Number.isFinite(n) ? n : null;
};

const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v));

const readInitialCameraFromUrl = (): { zoom?: number; centerWorld?: { x: number; y: number } } => {
  const sp = new URLSearchParams(window.location.search);

  // zoom: ?z= eller ?zoom=
  const zoomParam = sp.get("z") ?? sp.get("zoom");
  const zoomParsed = zoomParam ? parseNum(zoomParam) : null;
  const zoom = zoomParsed != null ? clamp(zoomParsed, 0.05, 20) : undefined;

  // pan: ?p= "x,y" eller ?pan= "x,y"
  const panParam = sp.get("p") ?? sp.get("pan");
  if (panParam) {
    const [xs, ys] = panParam.split(/[;,]/).map((v) => v.trim());
    const x = xs ? parseNum(xs) : null;
    const y = ys ? parseNum(ys) : null;
    if (x != null && y != null) {
      return { zoom, centerWorld: { x, y } };
    }
  }

  // alt pan: ?x=&y= eller ?cx=&cy=
  const xParam = sp.get("x") ?? sp.get("cx");
  const yParam = sp.get("y") ?? sp.get("cy");
  const x = xParam ? parseNum(xParam) : null;
  const y = yParam ? parseNum(yParam) : null;

  if (x != null && y != null) {
    return { zoom, centerWorld: { x, y } };
  }

  return { zoom };
};

const formatNum = (n: number) => String(Math.round(n * 1000) / 1000);

export const FloorViewer: React.FC = () => {
  const dispatch = useAppDispatch();

  const projectNo = useAppSelector(selectProjectNo);
  useGetMultiplacerDevicetypesQuery({ projectNo });
  usePostMcbimItemsQuery(useAppSelector(selectGetMcBimCeilingDevicesArgs) ?? skipToken);
  usePostMcbimItemsQuery(useAppSelector(selectGetMcBimItemsArkArgs) ?? skipToken);

  const pixPolygons = useAppSelector(selectFinalPixPolygons);
  const pixPaths = useAppSelector(selectPixPaths);
  const rotation = useAppSelector(selectModRotation);
  const pixActionState = useAppSelector(selectPixActionState);

  const extentKey = useAppSelector(selectFloorExtentKey);
  const zoomExtentNonce = useAppSelector((state) => state.design.zoomExtentNonce);
  const zoomExtentKey = `${extentKey ?? "no-extent"}:${zoomExtentNonce}`;

  // ---------------------------------------------------------------------------
  // ✅ Initial camera from URL (apply only once, then go uncontrolled)
  // ---------------------------------------------------------------------------
  const initialFromUrl = useMemo(() => readInitialCameraFromUrl(), []);
  const hasUrlCamera = initialFromUrl.zoom != null && initialFromUrl.centerWorld != null;

  // Om URL har kamera -> vi applicerar initialt en kort stund, sen släpper vi (uncontrolled)
  const [applyInitialCamera, setApplyInitialCamera] = useState<boolean>(hasUrlCamera);

  useEffect(() => {
    if (!applyInitialCamera) return;
    if (!pixPolygons || pixPolygons.length === 0) return;

    const id = window.setTimeout(() => setApplyInitialCamera(false), 1000);
    return () => window.clearTimeout(id);
  }, [applyInitialCamera, pixPolygons]);

  // Bygg props så vi aldrig skickar "undefined" som värde; vi skickar antingen ett number eller inget alls
  const initialCameraProps = useMemo(() => {
    if (!applyInitialCamera) return {};
    if (!hasUrlCamera) return {}; // URL saknar komplett kamera -> låt zoom extent (default)
    return {
      zoom: initialFromUrl.zoom as number,
      centerWorld: initialFromUrl.centerWorld as { x: number; y: number },
    };
  }, [applyInitialCamera, hasUrlCamera, initialFromUrl.centerWorld, initialFromUrl.zoom]);

  // ---------------------------------------------------------------------------
  // ✅ Update URL in realtime (no debounce), but skip if unchanged.
  // ---------------------------------------------------------------------------
  const lastUrlCamRef = useRef<string>("");

  return (
    <Box
      w="100%"
      h="100%"
      style={{
        display: "flex",
        flexDirection: "column",
        position: "relative",
        borderTop: "1px solid #ccc",
      }}
    >
      <Box style={{ flex: 1, overflow: "hidden" }}>
        <StatsBar />
        <Box h="100%" w="100%">
          <PixBimViewer
            useLeftClickToPan={IS_DEV}
            angle={rotation}
            polygons={pixPolygons}
            paths={pixPaths}
            action={pixActionState}
            zoomExtentKey={zoomExtentKey}
            onMoveStart={(id) => dispatch(thunkStartMove(id))}
            onRightMenuClick={(action, polygon) =>
              dispatch(thunkHandleRightMenu(action as RIGHT_CLICK_DEVICE_MENU_ENUM, polygon.id))
            }
            onClick={(id) => dispatch(thunkClickOnAPixPolygon(id))}
            onDragAndDrop={(id, points) => dispatch(thunkMoveDeviceOnDragEnd({ id, points }))}
            onDragSelected={(ids) => dispatch(dragSelectEndThunk(ids))}
            // ✅ Apply URL camera only once
            {...initialCameraProps}
            // ✅ Update URL so it can be shared
            onCameraChange={(camera) => {
              // Guard: din typ kan tillåta undefined även om viewport i praktiken alltid skickar values
              if (camera.zoom == null) return;
              if (!camera.centerWorld) return;

              const z = formatNum(camera.zoom);
              const p = `${formatNum(camera.centerWorld.x)},${formatNum(camera.centerWorld.y)}`;
              const camKey = `z=${z}&p=${p}`;

              if (lastUrlCamRef.current === camKey) return;
              lastUrlCamRef.current = camKey;

              const sp = new URLSearchParams(window.location.search);
              sp.set("z", z);
              sp.set("p", p);

              // Rensa alternativa varianter så URL blir stabil
              sp.delete("zoom");
              sp.delete("pan");
              sp.delete("x");
              sp.delete("y");
              sp.delete("cx");
              sp.delete("cy");

              const newUrl = `${window.location.pathname}?${sp.toString()}${window.location.hash ?? ""}`;
              window.history.replaceState(null, "", newUrl);
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

const StatsBar: React.FC = () => {
  const floorAndJob = useAppSelector((state) => state.design.floorAndJob);
  const nrOfMcBimSpaces = useAppSelector(selectnrOfMcbimSpaces);
  const nrOfDevices = useAppSelector(selectNrOfDevices);

  if (floorAndJob == null) return null;

  return (
    <Flex justify="center" bg="white">
      <h6 style={{ margin: "0 10px" }}>Etasje: {floorAndJob.floorName}</h6>
      <h6 style={{ margin: "0 10px" }}>Rom: {nrOfMcBimSpaces}</h6>
      <h6 style={{ margin: "0 10px" }}>Komponenter: {nrOfDevices}</h6>
    </Flex>
  );
};
